import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import ContactRow from '../components/ContactRow';
import { ContractEditor } from '../components/ContractEditor';
import { CustomerDuplicates } from '../components/CustomerDuplicates';
import ListEnrichButton from '../components/ListEnrichButton';
import {
  ListChecks, Plus, Loader2, Users, Building2, Zap, Trash2, Filter, Search, Mail, Download
} from 'lucide-react';
import { FilterBuilder, emptyGroup } from '../components/FilterBuilder';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Live "X match" count for a rule tree, debounced.
const RulePreview = ({ objectType, rules }) => {
  const [count, setCount] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      axios.post(`${API}/lists/preview`, { object_type: objectType, rules })
        .then(r => setCount(r.data.total))
        .catch(() => setCount(null))
        .finally(() => setLoading(false));
    }, 450);
    return () => clearTimeout(t);
  }, [objectType, rules]);
  return (
    <div className="flex items-center gap-2 text-sm" data-testid="rule-preview">
      <span className="text-slate-400">Matching now:</span>
      {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />
        : <span className="font-semibold text-violet-300" data-testid="rule-preview-count">{count ?? '-'}</span>}
      <span className="text-slate-500">{objectType === 'contact' ? 'contacts' : 'companies'}</span>
    </div>
  );
};

const Lists = () => {
  const navigate = useNavigate();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [campaigningId, setCampaigningId] = useState(null);
  const [exportList, setExportList] = useState(null);
  const [exportCols, setExportCols] = useState(null);
  const [exportSelected, setExportSelected] = useState(new Set());
  const [exportFormat, setExportFormat] = useState('csv');
  const [exporting, setExporting] = useState(false);
  const [activeList, setActiveList] = useState(null);
  const [listSearch, setListSearch] = useState('');
  const [form, setForm] = useState({
    name: '', object_type: 'contact', list_type: 'static',
  });
  const [useFilter, setUseFilter] = useState(false);   // static lists: build membership from a filter
  const [rules, setRules] = useState(emptyGroup());

  const fetchLists = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/lists`);
      setLists(res.data.lists || []);
    } catch { toast.error('Failed to load lists'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchLists(); }, [fetchLists]);

  const resetForm = () => {
    setForm({ name: '', object_type: 'contact', list_type: 'static' });
    setRules(emptyGroup());
    setUseFilter(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('List name required'); return; }
    setCreating(true);
    try {
      const payload = { name: form.name.trim(), object_type: form.object_type, list_type: form.list_type };
      // Active lists always use the rule tree; static lists optionally snapshot from it.
      if (form.list_type === 'active' || (form.list_type === 'static' && useFilter)) {
        payload.rules = rules;
      }
      const res = await axios.post(`${API}/lists`, payload);
      toast.success(`List "${res.data.name}" created (${res.data.member_count} members)`);
      setShowCreate(false);
      resetForm();
      fetchLists();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create list'); }
    finally { setCreating(false); }
  };

  const handleDelete = async (l, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete list "${l.name}"?`)) return;
    try {
      await axios.delete(`${API}/lists/${l.id}`);
      toast.success('List deleted');
      fetchLists();
    } catch { toast.error('Failed to delete'); }
  };

  const openExport = async (l, e) => {
    e.stopPropagation();
    setExportList(l);
    setExportCols(null);
    setExportFormat('csv');
    try {
      const { data } = await axios.get(`${API}/lists/${l.id}/columns`);
      setExportCols(data.columns);
      setExportSelected(new Set(data.columns.map((c) => c.key)));
    } catch {
      toast.error('Could not load export options');
      setExportList(null);
    }
  };

  const toggleField = (key) => {
    setExportSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const doExport = async () => {
    if (!exportList) return;
    const cols = (exportCols || []).filter((c) => exportSelected.has(c.key)).map((c) => c.key);
    if (cols.length === 0) { toast.error('Pick at least one field'); return; }
    setExporting(true);
    try {
      const res = await axios.get(`${API}/lists/${exportList.id}/export`, {
        params: { format: exportFormat, fields: cols.join(',') },
        responseType: 'blob',
      });
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      const fname = match ? match[1] : `${exportList.name}.${exportFormat}`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${exportFormat.toUpperCase()} downloaded`);
      setExportList(null);
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleCreateCampaign = async (l, e) => {
    e.stopPropagation();
    setCampaigningId(l.id);
    try {
      const res = await axios.post(`${API}/email-campaigns`, { name: `${l.name} campaign`, list_id: l.id });
      toast.success('Campaign created - opening the builder');
      navigate(`/admin/campaigns/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create campaign');
      setCampaigningId(null);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="lists-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-4xl text-white mb-2 flex items-center gap-3">
            <ListChecks className="w-8 h-8 text-violet-400" />
            Lists
          </h1>
          <p className="text-slate-400">Segment contacts & companies. Active lists update themselves.</p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button className="bg-violet-600 hover:bg-violet-500" data-testid="create-list-btn">
              <Plus className="w-4 h-4 mr-2" /> New List
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-white">New List</DialogTitle>
              <DialogDescription className="text-slate-400">Static lists hold members you add. Active lists auto-fill from rules.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-3 mt-2">
              <div><Label className="text-slate-300">Name *</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-slate-950 border-slate-800" placeholder="e.g. Outstanding CQC care homes" data-testid="list-name-input" required />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="text-slate-300">Object</Label>
                  <Select value={form.object_type} onValueChange={v => setForm({ ...form, object_type: v })}>
                    <SelectTrigger className="bg-slate-950 border-slate-800" data-testid="list-object-type"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      <SelectItem value="contact">Contacts</SelectItem>
                      <SelectItem value="company">Companies</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-slate-300">Type</Label>
                  <Select value={form.list_type} onValueChange={v => setForm({ ...form, list_type: v })}>
                    <SelectTrigger className="bg-slate-950 border-slate-800" data-testid="list-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      <SelectItem value="static">Static (manual)</SelectItem>
                      <SelectItem value="active">Active (auto)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {form.list_type === 'static' && (
                <div className="flex items-center justify-between bg-slate-950 border border-slate-800 rounded-lg px-3 py-2.5">
                  <Label className="text-slate-300 text-sm flex items-center gap-2"><Filter className="w-3.5 h-3.5" /> Add people matching a filter</Label>
                  <Switch checked={useFilter} onCheckedChange={setUseFilter} data-testid="static-use-filter-toggle" />
                </div>
              )}

              {(form.list_type === 'active' || (form.list_type === 'static' && useFilter)) && (
                <div className="space-y-3">
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <Filter className="w-3 h-3" />
                    {form.list_type === 'active'
                      ? 'Members auto-update to everyone matching these rules:'
                      : 'A snapshot of everyone matching these rules will be added (you can still edit membership later):'}
                  </p>
                  <FilterBuilder objectType={form.object_type} value={rules} onChange={setRules} />
                  <RulePreview objectType={form.object_type} rules={rules} />
                </div>
              )}

              <DialogFooter className="gap-2 pt-2">
                <Button type="button" variant="ghost" onClick={() => setShowCreate(false)} disabled={creating}>Cancel</Button>
                <Button type="submit" className="bg-violet-600 hover:bg-violet-500" disabled={creating} data-testid="submit-new-list">
                  {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</> : <><Plus className="w-4 h-4 mr-2" /> Create</>}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-20" data-testid="lists-loading"><Loader2 className="w-8 h-8 animate-spin text-slate-500" /></div>
      ) : lists.length === 0 ? (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="text-center py-16 text-slate-500">No lists yet. Click "New List" to segment your CRM.</CardContent>
        </Card>
      ) : (
        <>
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              value={listSearch}
              onChange={(e) => setListSearch(e.target.value)}
              placeholder="Search lists by name…"
              className="pl-9 bg-slate-900 border-slate-800 text-white"
              data-testid="lists-search-input"
            />
          </div>
          {(() => {
            const q = listSearch.trim().toLowerCase();
            const shown = q ? lists.filter((l) => (l.name || '').toLowerCase().includes(q)) : lists;
            if (shown.length === 0) {
              return <p className="text-sm text-slate-500 py-8 text-center" data-testid="lists-no-match">No lists match “{listSearch}”.</p>;
            }
            return (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {shown.map(l => (
                  <div
                    key={l.id}
                    onClick={() => setActiveList(l)}
                    className="bg-slate-900/60 hover:bg-slate-900 border border-slate-800 rounded-lg p-4 cursor-pointer transition-colors"
                    data-testid={`list-card-${l.id}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <p className="text-sm text-white font-medium">{l.name}</p>
                      <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                        <button onClick={(e) => openExport(l, e)} className="text-slate-500 hover:text-[#00A79D] transition-colors" title="Export (CSV or Excel)" data-testid={`export-list-${l.id}`}>
                          <Download className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={(e) => handleDelete(l, e)} className="text-slate-600 hover:text-rose-400" data-testid={`delete-list-${l.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge variant="outline" className={`text-[10px] ${l.list_type === 'active' ? 'border-violet-700/50 text-violet-400' : 'border-slate-700 text-slate-400'}`}>
                        {l.list_type === 'active' ? <><Zap className="w-2.5 h-2.5 mr-1" /> Active</> : 'Static'}
                      </Badge>
                      <Badge variant="outline" className="text-[10px] border-slate-700 text-slate-400">
                        {l.object_type === 'contact' ? <Users className="w-2.5 h-2.5 mr-1" /> : <Building2 className="w-2.5 h-2.5 mr-1" />}
                        {l.object_type === 'contact' ? 'Contacts' : 'Companies'}
                      </Badge>
                      <span className="text-xs text-slate-500 ml-auto">{l.member_count} members</span>
                    </div>
                    {l.object_type === 'contact' && (
                      <button
                        onClick={(e) => handleCreateCampaign(l, e)}
                        disabled={campaigningId === l.id}
                        className="mt-3 w-full flex items-center justify-center gap-1.5 text-xs font-medium text-violet-300 hover:text-white bg-violet-600/10 hover:bg-violet-600 border border-violet-700/40 rounded-md py-1.5 transition-colors disabled:opacity-60"
                        data-testid={`create-campaign-${l.id}`}
                      >
                        {campaigningId === l.id
                          ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Creating…</>
                          : <><Mail className="w-3.5 h-3.5" /> Create campaign</>}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}
        </>
      )}

      <ListMembersDialog list={activeList} onClose={() => setActiveList(null)} onChanged={fetchLists} />

      <Dialog open={!!exportList} onOpenChange={(o) => !o && setExportList(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg" data-testid="export-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Export “{exportList?.name}”</DialogTitle>
            <DialogDescription className="text-slate-400">Choose a format and which fields to include.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2" data-testid="export-format">
              {['csv', 'xlsx'].map((f) => (
                <button key={f} onClick={() => setExportFormat(f)} data-testid={`export-format-${f}`}
                  className={`px-4 py-2 rounded-md text-sm font-semibold border transition-colors ${exportFormat === f ? 'bg-[#00A79D] text-white border-[#00A79D]' : 'border-slate-700 text-slate-300 hover:border-slate-500'}`}>
                  {f === 'csv' ? 'CSV (.csv)' : 'Excel (.xlsx)'}
                </button>
              ))}
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-slate-300">Fields</Label>
                <div className="flex gap-3 text-xs">
                  <button className="text-[#00A79D] hover:underline" onClick={() => setExportSelected(new Set((exportCols || []).map((c) => c.key)))}>Select all</button>
                  <button className="text-slate-400 hover:underline" onClick={() => setExportSelected(new Set())}>Clear</button>
                </div>
              </div>
              {!exportCols ? (
                <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>
              ) : (
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 max-h-[40vh] overflow-y-auto pr-1">
                  {exportCols.map((c) => (
                    <label key={c.key} className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer" data-testid={`export-field-${c.key}`}>
                      <input type="checkbox" checked={exportSelected.has(c.key)} onChange={() => toggleField(c.key)} className="accent-[#00A79D]" />
                      {c.label}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setExportList(null)} className="text-slate-300">Cancel</Button>
            <Button onClick={doExport} disabled={exporting} className="bg-[#00A79D] hover:bg-[#00857D] text-white" data-testid="export-download-btn">
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Download {exportFormat.toUpperCase()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ContactContract = ({ m, onSaved }) => (
  <ContractEditor key={`${m.contract_company_id}-${m.contract?.amount}`} companyId={m.contract_company_id} contract={m.contract} onSaved={onSaved} />
);

const ContractTotal = ({ list, refreshKey }) => {
  const [t, setT] = useState(null);
  useEffect(() => {
    axios.get(`${API}/lists/${list.id}/contract-total`).then((r) => setT(r.data)).catch(() => setT(null));
  }, [list.id, refreshKey]);
  if (!t) return null;
  const former = (list.rules?.conditions || []).some((c) => c.value === 'former');
  const gbp = (n) => `£${Number(n).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
  return (
    <div className="flex items-end justify-between gap-4 bg-slate-950 border border-slate-800 rounded-lg px-4 py-3" data-testid="contract-total">
      <div>
        <p className="text-[11px] uppercase tracking-wider text-slate-500">{former ? 'Used to be worth' : 'Total contract value'}</p>
        <p className="text-2xl font-semibold text-white" data-testid="contract-total-yearly">{gbp(t.yearly_total)}<span className="text-sm font-normal text-slate-400"> a year</span></p>
        {t.one_off_total > 0 && <p className="text-xs text-slate-400">plus {gbp(t.one_off_total)} one-off</p>}
      </div>
      <p className="text-xs text-slate-500 text-right" data-testid="contract-total-coverage">
        {t.with_amount} of {t.companies} {t.companies === 1 ? 'company has' : 'companies have'} an amount
        {t.with_amount < t.companies && <><br />add the rest below for a full total</>}
      </p>
    </div>
  );
};

const ContactTypeFix = ({ list, onDone }) => {
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/lists/${list.id}/contact-type-fix`).then((r) => setCount(r.data.count || 0)).catch(() => {});
  }, [list.id]);
  if (!count) return null;
  const run = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/lists/${list.id}/contact-type-fix`);
      toast.success(`${r.data.updated} contacts tidied: now Tech suppliers, old care-provider tag removed`);
      setCount(0);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update contact types'); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5" data-testid="contact-type-fix-banner">
      <span className="text-xs text-amber-900">{count} {count === 1 ? 'person on this list needs' : 'people on this list need'} tidying: not marked as a Tech supplier, or still tagged "Care Provider Audience".</span>
      <Button size="sm" onClick={run} disabled={busy} className="bg-amber-500 hover:bg-amber-400 text-slate-950 shrink-0" data-testid="contact-type-fix-btn">
        {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}Tidy them all
      </Button>
    </div>
  );
};

const ListMembersDialog = ({ list, onClose, onChanged }) => {
  const [members, setMembers] = useState([]);
  const [showContract, setShowContract] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [rules, setRules] = useState(emptyGroup());
  const [saving, setSaving] = useState(false);

  const loadMembers = useCallback(() => {
    if (!list) { setMembers([]); return; }
    setLoading(true);
    axios.get(`${API}/lists/${list.id}/members`, { params: { page_size: 100 } })
      .then(res => { setMembers(res.data.members || []); setShowContract(!!res.data.show_contract); })
      .catch(() => toast.error('Failed to load members'))
      .finally(() => setLoading(false));
  }, [list]);

  useEffect(() => {
    loadMembers();
    setEditing(false);
    setRules((list && list.rules) ? list.rules : emptyGroup());
  }, [list, loadMembers]);

  const saveRules = async () => {
    setSaving(true);
    try {
      if (list.list_type === 'active') {
        await axios.patch(`${API}/lists/${list.id}`, { rules });
        toast.success('Filters updated');
      } else {
        const res = await axios.post(`${API}/lists/${list.id}/add-matching`, { rules });
        toast.success(`Added ${res.data.added} matching ${list.object_type === 'contact' ? 'contacts' : 'companies'}`);
      }
      setEditing(false);
      loadMembers();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!list} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="list-members-dialog">
        {list && (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-white">{list.name}</DialogTitle>
              <DialogDescription className="text-slate-400">
                {list.list_type === 'active' ? 'Auto-updating list' : 'Static list'} · {list.member_count} {list.object_type === 'contact' ? 'contacts' : 'companies'}
              </DialogDescription>
            </DialogHeader>

            <div className="flex justify-end items-start gap-2 flex-wrap">
              <ListEnrichButton key={list.id} listId={list.id} autoFill={list.auto_fill_weekly} onDone={loadMembers} onAutoChange={onChanged} />
              <Button size="sm" variant="outline" className="bg-slate-950 border-slate-700 text-slate-200"
                      onClick={() => setEditing(e => !e)} data-testid="list-edit-filters-btn">
                <Filter className="w-3.5 h-3.5 mr-1.5" />
                {editing ? 'Hide filters' : (list.list_type === 'active' ? 'Edit filters' : 'Add matching by filter')}
              </Button>
            </div>

            {editing && (
              <div className="space-y-3 bg-slate-950 border border-slate-800 rounded-lg p-3" data-testid="list-edit-filters-panel">
                <FilterBuilder objectType={list.object_type} value={rules} onChange={setRules} />
                <div className="flex items-center justify-between">
                  <RulePreview objectType={list.object_type} rules={rules} />
                  <Button size="sm" className="bg-violet-600 hover:bg-violet-500" onClick={saveRules} disabled={saving} data-testid="list-save-filters-btn">
                    {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</>
                      : list.list_type === 'active' ? 'Save filters' : 'Add matching'}
                  </Button>
                </div>
              </div>
            )}

            {showContract && <ContractTotal list={list} refreshKey={members.map((m) => m.contract?.amount ?? '').join(',')} />}
            {showContract && <ContactTypeFix key={list.id} list={list} onDone={loadMembers} />}
            {showContract && <CustomerDuplicates onDone={loadMembers} />}

            {loading ? (
              <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
            ) : members.length === 0 ? (
              <p className="text-sm text-slate-600 italic py-6 text-center">No members.</p>
            ) : (
              <div className="space-y-2 max-h-[48vh] overflow-y-auto pr-1">
                {members.map(m => showContract ? (
                  <div key={m.id} className="flex items-center gap-3" data-testid={`customer-row-${m.id}`}>
                    <div className="flex-1 min-w-0"><ContactRow m={m} tone="dark" objectType={list.object_type} /></div>
                    <ContactContract m={m} onSaved={(k) => setMembers((ms) => ms.map((x) => (x.contract_company_id === m.contract_company_id ? { ...x, contract: k } : x)))} />
                  </div>
                ) : <ContactRow key={m.id} m={m} tone="dark" objectType={list.object_type} />)}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default Lists;
